import React, { useState } from 'react';
import InputGroup from 'react-bootstrap/InputGroup';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import { settingsFunctions } from "../../../../functions/settings";
import { useAuth } from "../../../../utils/Auth";
import FloatingLabelInput from '../../../Custom/FloatingLabelInput';
import FloatingLabelTextarea from '../../../Custom/FloatingLabelTextarea';
import enterpriseAPI from '../../../../utils/API/enterpriseAPI';
import jobsAPI from '../../../../utils/API/jobsAPI';
import parser from 'parse-address';
import skillsAutoComplete from '../../../../utils/skillsAutoComplete';
import './TopCardEditor.css';

function TopCardEditor(props) {
    const { vcardObj, skillsArr, setSkillsArr, setShowForm, setStep, enterprise, permissions, uid, jobs } = props;
    const { name_first, name_last, skills, address, location, organization_name, job_title, tagline, bio } = vcardObj;
    const { updateVcardObject } = settingsFunctions;
    const { getUpdatedUserVcard } = useAuth();

    const [ firstName, setFirstName ] = useState(name_first ? name_first : "");
    const [ lastName, setLastName ] = useState(name_last ? name_last : "");
    const [ newAddress, setNewAddress ] = useState(address ? address : "");
    const [ org, setOrg ] = useState(organization_name ? organization_name : "");
    const [ jobTitle, setJobTitle ] = useState(job_title ? job_title : "");
    const [ newTagline, setNewTagline ] = useState(tagline ? tagline : "");
    const [ newBio, setNewBio ] = useState(bio ? bio : "");
    const [ newSkill, setNewSkill ] = useState("");
    const [ showSkills, setShowSkills ] = useState(skills);
    const [ showSpinner, setShowSpinner ] = useState(false);
    
    const [ tempSkills, setTempSkills ] = useState(skillsArr.length > 0 ? skillsArr : []);
    const [ newLocation, setNewLocation ] = useState(location);
    const [ allDisabled ] = useState(permissions.edit_name === false && permissions.edit_tagline === false && permissions.edit_address === false && permissions.edit_bio === false);

    const addSkill = () => {
        if(tempSkills?.includes(newSkill)){
            document.getElementById("top-card-skill").value = "";
        } else {
            setTempSkills(old => [...old, newSkill]);
            document.getElementById("top-card-skill").value = "";
        };
    };

    const removeSkill = (e) => {
        const skillToRemove = e.target.name;
        if (tempSkills.includes(skillToRemove)) {
            setTempSkills(tempSkills.filter(item => item !== skillToRemove))
        };
    };

    const resetTopCard = () => {
        setFirstName(name_first);
        setLastName(name_last);
        setNewAddress(address);
        setOrg(organization_name);
        setJobTitle(job_title);
        setNewTagline(tagline);
        setNewBio(bio);
    };

    const setLocationFunction = () => {
        if (newAddress !== address) {
            const adr = newAddress.trim();
            
            if (!isNaN(adr.charAt(0)) && adr.includes(',')) {
                let parsed = parser.parseLocation(adr)
                setNewLocation(parsed.city !== undefined ? `${parsed.city}, ${parsed.state}` : null);
            } else if (adr.includes(',') && (adr.match(/,/g) || []).length < 2) {
                setNewLocation(adr)
            } else if (adr.includes(',') && (adr.match(/,/g) || []).length < 3) {
                setNewLocation(adr.substring(adr.indexOf(',') + 1))
            } else {
                setNewLocation(`${adr}`);
            };
        };
    };

    const saveObject = async () => {
        const update = {};
        const entObj = {};

        // location update
        if (newLocation !== location) {
            update.location = newLocation;
        };

        // org permission check
        if (permissions.edit_organization && org !== organization_name) {
            update.organization_name = org;
        };

        // job title permission check
        if (permissions.edit_job_title && jobTitle !== job_title) {
            update.job_title =jobTitle;

            if (enterprise) {
                entObj.job_title = jobTitle;
            };
        };

        //Address permission check
        if (permissions.edit_address && newAddress.trim() !== address) {
            update.address = newAddress?.trim();
        };

        // Name permission check
        if (permissions.edit_name){
            if (firstName !== name_first) {
                update.name_first = firstName.length > 0 ? firstName : "";
                
                if (enterprise) {
                    entObj.name_first = firstName;
                };
            };
            
            if (lastName !== name_last) {
                update.name_last = lastName.length > 0 ? lastName : "";

                if (enterprise) {
                    entObj.name_last = lastName;
                };
            };
        };

        // Tagline permission check
        if (permissions.edit_tagline && newTagline !== tagline) {
            update.tagline = newTagline;
        };

        // Bio component permission check
        if (permissions.edit_bio && newBio !== bio) {
            update.bio = newBio;
            
            if (enterprise) {
                entObj.bio = newBio;
            };
        };
        
        // skills permission check
        if (permissions.edit_skills && skillsArr.length !== tempSkills.length) {
            update.skills = (jobs && skillsArr.length > 0 && tempSkills > 0) ? showSkills : jobs && skillsArr.length > 0 ? showSkills : tempSkills.length > 0 ? true : false;
        };

        // Save updates to enterprise object if applicable
        if (enterprise && Object.keys(entObj).length > 0) {
           await enterpriseAPI.updateEnterpriseAccount(uid, entObj);
        };

        // Save skills if skills doesn't equal each other
        if (skillsArr.length !== tempSkills.length) {
            setSkillsArr(tempSkills)
            await jobsAPI.updateUserSkills(tempSkills);
        };

        await saveTopCard(update);
    };

    const saveTopCard = async (obj) => {
        setShowSpinner(true);
        await updateVcardObject(obj, uid)
            .then(async () => {
                await getUpdatedUserVcard().then(() => {
                    setTempSkills([]);
                    setShowForm("");
                });
            }).catch((err) => console.error(err.response));
    };

    return (
        <div className='top-card-editor-container'>
            <Card.Header className='top-card-editor-header'>
                <div className='top-card-editor-back-btn' onClick={() => window.location.pathname.includes('wizard') ? setStep(prev => (prev - 1)) : setShowForm("")}>
                    <i className='fas fa-arrow-left'></i>
                    Back
                </div>
                Edit Top Card
                <div className='top-card-editor-reset-btn' onClick={e => {e.preventDefault(); resetTopCard()}}>
                    Reset
                </div>
            </Card.Header>
            <div className='top-card-editor-inner-container' >
                <InputGroup className='top-card-editor-names'>
                    <InputGroup>
                        <FloatingLabelInput
                            state={firstName}
                            setState={setFirstName}
                            id='top-card-firstName'
                            type='text'
                            label='First Name'
                            disabled={!permissions.edit_name}
                            style={{borderRadius: '2.5rem'}}
                        />
                    </InputGroup>
                    <InputGroup>
                        <FloatingLabelInput
                            state={lastName}
                            setState={setLastName}
                            id='top-card-lastName'
                            type='text'
                            label='Last Name'
                            disabled={!permissions.edit_name}
                            style={{borderRadius: '2.5rem'}}
                        />
                    </InputGroup>
                </InputGroup>
                <InputGroup className='top-card-editor-jobs'>
                    <InputGroup>
                        <FloatingLabelInput
                            state={org}
                            setState={setOrg}
                            id='top-card-org'
                            type='text'
                            label='Organization'
                            disabled={!permissions.edit_organization}
                            style={{borderRadius: '2.5rem'}}
                        />
                    </InputGroup>
                    <InputGroup>
                        <FloatingLabelInput
                            state={jobTitle}
                            setState={setJobTitle}
                            id='top-card-jobTitle'
                            type='text'
                            label='Job Title'
                            disabled={!permissions.edit_job_title}
                            style={{borderRadius: '2.5rem'}}
                        />
                    </InputGroup>
                </InputGroup>
                <InputGroup>
                    <FloatingLabelInput
                        state={newTagline}
                        setState={setNewTagline}
                        id='top-card-tagline'
                        type='text'
                        label='Tagline(s)'
                        maxLength='55'
                        placeholder='Add your tagline(s)!'
                        disabled={!permissions.edit_tagline}
                        style={{borderRadius: '2.5rem'}}
                    />
                </InputGroup>
                <InputGroup>
                    <FloatingLabelInput
                        state={newAddress}
                        setState={setNewAddress}
                        id='top-card-address'
                        type='text'
                        label='Address'
                        placeholder='ex. 741 Spiral Blvd, Hastings, MN, 55033'
                        onKeyUp={e => {e.preventDefault(); setLocationFunction();}}
                        disabled={!permissions.edit_address}
                        style={{borderRadius: '2.5rem'}}
                    />
                </InputGroup>
                <InputGroup>
                    <FloatingLabelTextarea
                        state={newBio}
                        setState={setNewBio}
                        id='top-card-bio'
                        label='Bio (About Me)'
                        placeholder='Let your audience know who you are!'
                        maxLength='10000'
                        disabled={!permissions.edit_bio}
                        style={{borderRadius: '1rem 1rem 1.5rem 1rem'}}
                    />
                </InputGroup>
                {jobs && 
                    <InputGroup className='mb-2 top-card-show-skills-wrap' >
                        <input className='top-card-show-skills' id='top-card-skills-checkbox' type='checkbox' checked={showSkills} onChange={e => setShowSkills(e.target.checked)} ></input>
                        <label htmlFor='top-card-skills-checkbox'>Show Skills on your Digital Business Card</label>
                    </InputGroup>
                }
                <InputGroup className='top-card-editor-skills'>
                    <label htmlFor='top-card-skill' className='top-card-skill-label fli-label fli-float'>Skills/Interests</label>
                    <input
                        value={newSkill}
                        onChange={e => setNewSkill(e.target.value)}
                        className='top-card-skill-input'
                        id='top-card-skill'
                        list='skill-autocomplete'
                        label='Skills/Interests'
                        type='search'
                        placeholder='Add a skill and click the +'
                        maxLength='20'
                        disabled={!permissions.edit_skills}
                    />
                    <datalist id='skill-autocomplete' className='top-card-skills-autocomplete-select' >
                        {skillsAutoComplete.filter(function(val) {
                            if (val.toLowerCase().includes(newSkill.toLowerCase()) && val.toLowerCase().startsWith(newSkill.charAt(0).toLowerCase()) && this.count < 9) {
                                this.count++
                                return val;
                            } else {
                                return null;
                            }
                        }, { count: 0 }).sort((a, b) => a - b).map((val, i) => (
                            <option key={val+i} value={val}>{val}</option>
                        ))}
                    </datalist>
                    <Button disabled={!permissions.edit_skills} className='top-card-editor-skill-btn' onClick={addSkill}><i className='fas fa-plus' style={{lineHeight: '1.5rem'}}></i></Button>
                </InputGroup>
                <div className='added-skills-list'>
                    {tempSkills?.length > 0 ?
                        tempSkills.map((skill, index) => (
                            <Button 
                                disabled={!permissions.edit_skills}
                                className='skills-tags'
                                key={index}
                                name={skill}
                                onClick={removeSkill}>
                                {skill}
                                <i className='far fa-plus ms-2'></i>
                            </Button>
                        ))
                        :
                        <></>
                    }
                </div>
                <div className='d-flex justify-content-center'>
                    <Button onClick={saveObject} className='top-card-editor-apply-btn' disabled={allDisabled || showSpinner} >{showSpinner ? <i className="fa-thin fa-spinner fa-spin-pulse"></i> : "Apply Changes"}</Button>
                </div>
            </div>
        </div>
    );
};

export default TopCardEditor;